import styled, { css } from 'styled-components';
import type { NotificationVariant } from './GatorCompactHeader.types';

export const Container = styled.div`
  ${({ theme }) => css`
    position: relative;
    width: 100%;
    min-height: 228px;
    margin: 0 auto;
    border-radius: 24px;
    background: linear-gradient(
      180deg,
      ${theme.v3.colors.brand.snappyBlue[50]} 0%,
      ${theme.v3.colors.brand.snappyBlue[80]} 50%,
      ${theme.v3.colors.brand.snappyBlue[90]} 100%
    );
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px 0 20px 0;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      padding: 12px 16px 20px;
      min-height: auto;
      border-radius: 0;
      background: linear-gradient(
        to bottom,
        ${theme.v3.colors.brand.snappyBlue[50]} 70%,
        ${theme.v3.colors.brand.snappyBlue[80]} 100%
      );
    }
  `}
`;

export const WhiteBg = styled.div`
  ${({ theme }) => css`
    position: absolute;
    inset: 92px 2px 2px 2px;
    border-radius: 23px;
    background: ${theme.v3.colors.neutral[100]};

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      display: none;
    }
  `}
`;

export const HeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1;
`;

export const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 1000px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

export const GreetingText = styled.span`
  ${({ theme }) => css`
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.medium};
    font-size: 20px;
    line-height: 1.2;
    color: ${theme.v3.colors.neutral[100]};
    white-space: nowrap;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      white-space: normal;
      font-size: 16px;
    }
  `}
`;

export const VersionTag = styled.div`
  ${({ theme }) => css`
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    padding: 2px 4px;
    background: ${theme.v3.colors.neutral[100]};
    border-radius: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      display: none;
    }
  `}
`;

export const VersionTagText = styled.span`
  ${({ theme }) => css`
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.bold};
    font-size: 12px;
    line-height: normal;
    color: ${theme.v3.colors.brand.snappyBlue[15]};
  `}
`;

export const CompactInputWrapper = styled.div<{ $isExpanded?: boolean }>`
  ${({ theme, $isExpanded }) => css`
    position: relative;
    margin-top: 12px;
    width: calc(100% - 32px);
    max-width: 640px;
    height: ${$isExpanded ? '120px' : '56px'};
    border-radius: ${$isExpanded ? '28px' : '1000px'};
    background: ${theme.v3.colors.neutral[100]};
    border: 1px solid ${theme.v3.colors.neutral[90]};
    filter: drop-shadow(2px 2px 4px rgba(147, 147, 147, 0.16));
    padding: 16px 12px 16px 20px;
    display: flex;
    align-items: ${$isExpanded ? 'flex-start' : 'center'};
    justify-content: space-between;
    cursor: text;
    z-index: 2;
    transition: height 100ms ease;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      box-sizing: border-box;
      width: 100%;
    }
  `}
`;

export const CompactInputField = styled.textarea<{ $isExpanded?: boolean }>`
  ${({ theme, $isExpanded }) => css`
    flex: 1 0 0;
    min-width: 0;
    align-self: ${$isExpanded ? 'stretch' : 'center'};
    border: none;
    outline: none;
    background: transparent;
    resize: none;
    overflow-y: ${$isExpanded ? 'auto' : 'hidden'};
    padding: 0;
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.regular};
    font-size: 16px;
    line-height: 1.5;
    color: ${theme.v3.colors.neutral[20]};

    &::placeholder {
      color: ${theme.v3.colors.neutral[40]};
    }
  `}
`;

export const AudioButton = styled.button<{ $isExpanded?: boolean }>`
  ${({ theme, $isExpanded }) => css`
    width: 36px;
    height: 36px;
    border-radius: 1000px;
    padding: 2px 5px;
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    align-self: ${$isExpanded ? 'flex-start' : 'center'};

    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }

    &:focus-visible {
      outline: 2px solid ${theme.v3.colors.gradients.aiTwilight[0]};
      outline-offset: 2px;
    }
  `}
`;

export const NotificationBar = styled.button<{ $variant?: NotificationVariant }>`
  ${({ theme, $variant }) => css`
    position: relative;
    margin-top: -24px;
    width: calc(100% - 32px);
    max-width: 640px;
    background: ${$variant === 'default'
    ? theme.v3.colors.brand.snappyBlue[95]
    : theme.v3.colors.brand.snappyYellow[95]};
    border: none;
    border-radius: 0 0 20px 20px;
    padding: 32px 16px 8px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 0;
    cursor: pointer;
    text-align: left;
    font: inherit;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      box-sizing: border-box;
      width: 100%;
    }
  `}
`;

export const NotificationTextGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  height: 20px;
`;

export const NewGatorTag = styled.div<{ $variant?: NotificationVariant }>`
  ${({ theme, $variant }) => css`
    padding: 2px 8px;
    border-radius: 16px;
    background: ${$variant === 'default'
    ? css`linear-gradient(90deg, ${theme.v3.colors.brand.snappyBlue[50]}, ${theme.v3.colors.brand.snappyBlue[70]})`
    : theme.v3.colors.brand.snappyYellow[80]};
    display: flex;
    align-items: center;
    flex-shrink: 0;
  `}
`;

export const NewGatorTagText = styled.span<{ $variant?: NotificationVariant }>`
  ${({ theme, $variant }) => css`
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.bold};
    font-size: 12px;
    line-height: normal;
    color: ${$variant === 'default'
    ? theme.v3.colors.neutral[100]
    : theme.v3.colors.brand.snappyYellow[5]};
    white-space: nowrap;
  `}
`;

export const NotificationText = styled.span<{ $hasShortText?: boolean }>`
  ${({ theme, $hasShortText }) => css`
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.medium};
    font-size: 14px;
    line-height: 1.5;
    color: ${theme.v3.colors.brand.snappyBlue[5]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      ${$hasShortText && css`
        display: none;
      `}
    }
  `}
`;

export const NotificationTextShort = styled.span`
  ${({ theme }) => css`
    display: none;
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.medium};
    font-size: 14px;
    line-height: 1.5;
    color: ${theme.v3.colors.brand.snappyBlue[5]};
    white-space: nowrap;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      display: inline;
    }
  `}
`;

export const SuggestedOptions = styled.div<{ $showMore?: boolean }>`
  ${({ theme, $showMore }) => css`
    position: relative;
    margin-top: 24px;
    width: calc(100% - 32px);
    max-width: 640px;
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: center;
    z-index: 1;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      width: 100%;
      gap: 12px;
      ${$showMore ? css`
        display: grid;
        grid-template-columns: repeat(2, 1fr);
      ` : css`
        display: flex;
        align-items: center;
        justify-content: flex-start;
      `}
    }
  `}
`;

export const SuggestionChip = styled.button<{ $hideOnMobile?: boolean; $spanFull?: boolean }>`
  ${({ theme, $hideOnMobile, $spanFull }) => css`
    background: ${theme.v3.colors.neutral[100]};
    border: 1px solid ${theme.v3.colors.neutral[90]};
    border-radius: 12px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font: inherit;

    &:hover {
      background: ${theme.v3.colors.neutral[98]};
    }

    &:focus-visible {
      outline: 2px solid ${theme.v3.colors.gradients.aiTwilight[0]};
      outline-offset: 2px;
    }

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      min-width: 0;
      justify-content: center;
      background: rgba(255, 255, 255, 0.16);
      border: none;

      &:hover {
        background: rgba(255, 255, 255, 0.24);
      }

      &:focus-visible {
        outline-color: ${theme.v3.colors.neutral[100]};
      }

      svg {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
      }

      ${$hideOnMobile && css`display: none;`}
      ${$spanFull && css`grid-column: 1 / -1;`}
    }
  `}
`;

export const SuggestionChipPlus = styled(SuggestionChip)`
  padding: 8px;
`;

export const SuggestionChipLabel = styled.span`
  ${({ theme }) => css`
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.medium};
    font-size: 14px;
    line-height: 1.5;
    letter-spacing: -0.154px;
    color: ${theme.v3.colors.neutral[20]};
    white-space: nowrap;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 12px;
      color: ${theme.v3.colors.neutral[100]};
    }
  `}
`;

export const SuggestionsList = styled.div`
  ${({ theme }) => css`
    position: relative;
    background: ${theme.v3.colors.neutral[100]};
    border-radius: 16px;
    width: calc(100% - 32px);
    max-width: 640px;
    padding: 0px 16px;
    margin-top: 20px;
    z-index: 1;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      box-sizing: border-box;
      width: 100%;
      background: transparent;
    }
  `}
`;

export const SuggestionsHeader = styled.p`
  ${({ theme }) => css`
    font-family: ${theme.v3.font.family.brand};
    font-size: 14px;
    font-weight: ${theme.v3.font.weights.regular};
    color: ${theme.v3.colors.neutral[50]};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    padding: 0px;
    margin: 0 0 12px 0px;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      color: ${theme.v3.colors.neutral[100]};
    }
  `}
`;

export const SuggestionItem = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 0;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    font: inherit;

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }

    &:hover:not(:disabled) {
      background: ${theme.v3.colors.neutral[95]};
    }

    &:focus-visible {
      outline: 2px solid ${theme.v3.colors.gradients.aiTwilight[0]};
      outline-offset: 2px;
    }

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.08);
      }
    }
  `}
`;

export const SuggestionItemText = styled.span`
  ${({ theme }) => css`
    flex: 1;
    font-family: ${theme.v3.font.family.brand};
    font-size: 16px;
    font-weight: ${theme.v3.font.weights.regular};
    color: ${theme.v3.colors.neutral[20]};
    line-height: 24px;
    min-width: 0;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      font-size: 14px;
      color: ${theme.v3.colors.neutral[100]};
    }
  `}
`;

export const SuggestionDivider = styled.hr`
  ${({ theme }) => css`
    height: 1px;
    background-color: ${theme.v3.colors.neutral[90]};
    border: none;
    margin: 0;

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      background-color: ${theme.v3.colors.brand.snappyBlue[60]};
    }
  `}
`;

export const LearnMoreButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
    width: 100%;
    padding: 12px 0;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: ${theme.v3.font.family.brand};
    font-size: 14px;
    font-weight: ${theme.v3.font.weights.medium};
    line-height: 1.5;
    color: ${theme.v3.colors.brand.snappyBlue[50]};

    &:focus-visible {
      outline: 2px solid ${theme.v3.colors.gradients.aiTwilight[0]};
      outline-offset: 2px;
    }

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      color: ${theme.v3.colors.neutral[100]};
    }
  `}
`;

export const RecordingContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
`;

export const RecordingLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
`;

export const RecordingAvatar = styled.div`
  ${({ theme }) => css`
    width: 32px;
    height: 32px;
    border-radius: 1000px;
    flex-shrink: 0;
    overflow: hidden;
    background: ${theme.v3.colors.brand.snappyBlue[95]};
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 4px 3px 0;

    img {
      width: 100%;
      display: block;
      object-fit: contain;
    }
  `}
`;

export const RecordingLabel = styled.span`
  ${({ theme }) => css`
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.medium};
    font-size: 14px;
    line-height: 1.5;
    color: ${theme.v3.colors.brand.snappyBlue[10]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

export const RecordingRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

export const RecordingTime = styled.span`
  ${({ theme }) => css`
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.medium};
    font-size: 14px;
    line-height: 1.5;
    color: ${theme.v3.colors.brand.snappyBlue[10]};
    font-variant-numeric: tabular-nums;
  `}
`;

export const RecordingActionButton = styled.button<{ $variant: 'cancel' | 'send' }>`
  ${({ theme, $variant }) => css`
    width: 32px;
    height: 32px;
    border-radius: 12px;
    padding: 0;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    background: ${$variant === 'cancel'
    ? theme.v3.colors.contextual.problem[50]
    : theme.v3.colors.brand.snappyBlue[50]};

    &:disabled {
      cursor: default;
      opacity: 0.6;
    }

    &:focus-visible {
      outline: 2px solid ${theme.v3.colors.gradients.aiTwilight[0]};
      outline-offset: 2px;
    }
  `}
`;
